import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import API from '../services/api';
import { formatIsoDate } from '../utils/formatDateValue';
import { validationApi } from '../services/validationApi';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import {
  SparklesIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SuggestionType = 'conflict' | 'fill' | 'enrich' | 'lifecycle' | 'escalation' | 'source';
type Decision = 'APPROVED' | 'REJECTED';
type Origin = 'dedup' | 'validation';

interface RawSuggestion {
  id: string;
  field: string;
  currentValue: unknown;
  suggestedValue: unknown;
  type: SuggestionType;
  priority?: number;
  groupId?: string | null;
  reason?: string;
  sourceUrl?: string;
  snippet?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
}

interface SourceCase {
  id: string;
  caseNumber: string;
  description?: string;
  sourceInformation?: string | null;
  dataSource?: string | null;
  createdAt?: string;
}

// A flattened, addressable suggestion. `container` is the match id (dedup) or
// validation-result id (validation) — the thing an Apply is grouped/dispatched by.
export interface Item extends RawSuggestion {
  origin: Origin;
  container: string;
  matchConfidence?: number;   // dedup only
  source: SourceCase;
}

const typeConfig: Record<SuggestionType, { label: string; color: string }> = {
  conflict: { label: 'Conflict', color: 'bg-red-100 text-red-700' },
  fill: { label: 'Fill', color: 'bg-blue-100 text-blue-700' },
  enrich: { label: 'Add', color: 'bg-green-100 text-green-700' },
  lifecycle: { label: 'Status', color: 'bg-amber-100 text-amber-700' },
  escalation: { label: 'Escalation', color: 'bg-red-100 text-red-800' },
  source: { label: 'Source', color: 'bg-indigo-100 text-indigo-700' },
};

const isEmptyVal = (v: unknown) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

const fmt = (v: unknown): string => {
  if (isEmptyVal(v)) return '(empty)';
  const asDate = formatIsoDate(v);
  if (asDate) return asDate;
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

// Which detail-page section a field belongs to (for section-level badges).
type Section = 'victims' | 'perpetrators' | 'violations';
// Bare ViolationDetail field names (validation emits these directly, not as "violationDetails[..].x").
const VIOLATION_DETAIL_FIELDS = new Set([
  'perpetratorKnown', 'perpetratorTypes', 'perpetratorDetails', 'cause',
  'whyVictimsKilled', 'howKillingCarriedOut', 'whyVictimsCriminalized',
  'accusations', 'policiesOrLawsUsed', 'accusationsLegallyFiled', 'courtFilingLocation', 'courtFilingDetails',
  'victimsInPrison', 'prisonConditions', 'victimsSituationIfNotInPrison',
  'harassmentPrior', 'harassmentPriorDetails', 'legalCaseFiled', 'legalCaseDetails', 'legalCaseWhyNot',
  'stateActionUndertaken', 'stateActionDetails', 'stateActionWhyNot',
  'decisionOnAccusations', 'decisionDetails', 'whoFiledAccusations', 'whoFiledAccusationsDetails',
]);
const sectionOf = (field: string): Section | null => {
  if (/^victims(\[|$)/.test(field)) return 'victims';
  if (/^perpetrators(\[|$)/.test(field)) return 'perpetrators';
  if (/^violationDetails(\[|$)/.test(field) || field === 'violationTypes' || field === 'otherViolationTypes' || VIOLATION_DETAIL_FIELDS.has(field.replace(/^violationDetails\[[^\]]+\]\./, ''))) return 'violations';
  return null;
};
const isEntityField = (field: string) => ['victims', 'perpetrators', 'violationDetails'].includes(field);

// Fields that are two halves of a single logical change and should render in one card (not as
// separate, duplicate-looking suggestions). violationTypes + otherViolationTypes together record an
// "OTHER" violation and its label.
const COUPLED_FIELD_KEY: Record<string, string> = {
  violationTypes: 'violation-type-pair',
  otherViolationTypes: 'violation-type-pair',
};

// Turn a raw field path into a human label, e.g.
//   "violationDetails[abc].perpetratorTypes" → "Violation detail · Perpetrator types"
//   "communityImpact" → "Community impact"   ·   "victims" → "New victim"
const CONTEXT_LABEL: Record<string, string> = { victims: 'Victim', perpetrators: 'Perpetrator', violationDetails: 'Violation detail' };
const ADD_LABEL: Record<string, string> = { victims: 'New victim', perpetrators: 'New perpetrator', violationDetails: 'New violation detail' };
const humanizeLeaf = (leaf: string) =>
  leaf
    .replace(/([A-Z]+)/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase())
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());
// Compact, readable snippet of a URL for link text (drops protocol + www, truncates).
const urlSnippet = (url: string, max = 46): string => {
  const s = url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
  return s.length > max ? `${s.slice(0, max)}…` : s;
};
const prettyField = (field: string): string => {
  if (ADD_LABEL[field]) return ADD_LABEL[field];
  const m = field.match(/^(victims|perpetrators|violationDetails)\[[^\]]+\]\.(.+)$/);
  if (m) return `${CONTEXT_LABEL[m[1]]} · ${humanizeLeaf(m[2])}`;
  return humanizeLeaf(field);
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface Ctx {
  canReview: boolean;
  loading: boolean;
  items: Item[];              // all PENDING (incl. source)
  substantiveCount: number;   // non-source pending — drives visibility/badges
  decisions: Record<string, Decision>;
  busyContainer: string | null;
  setDecision: (item: Item, decision: Decision) => void;
  applyItems: (items: Item[]) => Promise<void>;
  decideAndApply: (items: Item[], decision: Decision) => Promise<void>;
  forFields: (fields: string[]) => Item[];
  forSection: (section: Section) => Item[];
  refresh: () => Promise<void>;
}

const SuggestionsContext = createContext<Ctx | null>(null);
export const useSuggestions = () => {
  const c = useContext(SuggestionsContext);
  if (!c) throw new Error('useSuggestions must be used within <SuggestionsProvider>');
  return c;
};

export function SuggestionsProvider({
  caseId, caseData, onApplied, children,
}: {
  caseId: string;
  caseData?: Record<string, any>;
  onApplied?: () => void;
  children: React.ReactNode;
}) {
  const { showToast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canReview = user?.role === 'ADMIN' || user?.role === 'APPROVER';

  // Once a case is APPROVED it is final — no proposed updates are shown anymore.
  const caseApproved = caseData?.status === 'APPROVED';
  // Validation findings apply to bot-produced (auto-sourced) secondary-data cases, actioned by APPROVER/ADMIN.
  const validationEligible =
    !caseApproved && caseData?.isPrimaryData === false && caseData?.isAutoSourced === true && (user?.role === 'ADMIN' || user?.role === 'APPROVER');

  const [dedupUpdates, setDedupUpdates] = useState<RawSuggestion[]>([]);
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [busyContainer, setBusyContainer] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Validation findings have no server-side "reject"; a rejected one is hidden locally for the session.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    // Proposed updates from related sources live in Case.metadata.ai.pendingUpdates (single source
    // of truth); this endpoint returns the PENDING ones. They still surface on an approved case — a
    // late-arriving duplicate can genuinely add info.
    if (!canReview) { setDedupUpdates([]); setLoading(false); return; }
    try {
      const res = await API.get(`/api/dedup/cases/${caseId}/updates`);
      setDedupUpdates(Array.isArray(res.data) ? res.data : []);
    } catch {
      setDedupUpdates([]);
    } finally {
      setLoading(false);
    }
  }, [caseId, canReview]);

  useEffect(() => { refresh(); }, [refresh]);

  // Validation findings (shares the react-query cache with AiValidationPanel).
  const { data: validationStatus } = useQuery<any>({
    queryKey: ['validation-status', caseId],
    queryFn: () => validationApi.getStatus(caseId),
    enabled: !!validationEligible,
  });

  const dedupItems: Item[] = useMemo(() => {
    // Flat pendingUpdates from metadata — all belong to THIS case, so they share one container (caseId).
    return (dedupUpdates || [])
      .filter((s) => s && s.status === 'PENDING')
      .map((s: any) => {
        const url: string | undefined = s.source || s.sourceUrl || undefined;
        return {
          ...s,
          sourceUrl: url,
          origin: 'dedup' as Origin,
          container: caseId,
          source: { id: '', caseNumber: 'Related source', dataSource: 'Related source', sourceInformation: url ?? null } as SourceCase,
        };
      });
  }, [dedupUpdates, caseId]);

  // When auto-apply is active, validation findings are handled by the AI Validation panel
  // (auto-applied inline, conflicts, notes) — so the legacy validation "proposed updates"
  // badges/bar are redundant and suppressed here. Dedup suggestions are unaffected.
  const autoApplyActive =
    ((caseData?.metadata?.ai?.autoApplied?.length ?? 0) > 0) ||
    ((caseData?.metadata?.ai?.conflicts?.length ?? 0) > 0);

  const validationItems: Item[] = useMemo(() => {
    if (!validationEligible || autoApplyActive) return [];
    const latest = validationStatus?.latest;
    if (!latest?.aiResponse?.findings) return [];
    const src: SourceCase = { id: latest.id, caseNumber: 'AI validation', dataSource: 'AI validation' };
    const out: Item[] = [];
    latest.aiResponse.findings.forEach((f: any, fi: number) => {
      (f.fixes || []).forEach((fix: any, xi: number) => {
        if (fix.applied) return;
        const id = `val::${latest.id}::${fi}::${xi}`;
        if (dismissed.has(id)) return;
        let type: SuggestionType;
        let currentValue: unknown = null;
        if (isEntityField(fix.field)) {
          type = 'enrich';
        } else {
          const cur = caseData ? caseData[fix.field] : undefined;
          if (!isEmptyVal(cur)) { type = 'conflict'; currentValue = cur; }
          else { type = 'fill'; }
        }
        out.push({
          id, field: fix.field, currentValue, suggestedValue: fix.suggestedValue,
          type, priority: type === 'conflict' ? 0 : 2, groupId: null,
          reason: f.message, sourceUrl: f.source, snippet: '', status: 'PENDING',
          origin: 'validation', container: latest.id, source: { ...src, sourceInformation: f.source ?? null },
        });
      });
    });
    return out;
  }, [validationEligible, autoApplyActive, validationStatus, dismissed, caseData]);

  const items = useMemo(() => [...dedupItems, ...validationItems], [dedupItems, validationItems]);
  const substantiveCount = useMemo(() => items.filter((i) => i.type !== 'source').length, [items]);

  // Deciding one item decides coupled items (same container + same groupId) together.
  const setDecision = useCallback((item: Item, decision: Decision) => {
    setDecisions((prev) => {
      const next = { ...prev };
      const siblings = item.groupId
        ? items.filter((i) => i.container === item.container && i.groupId === item.groupId)
        : [item];
      for (const s of siblings) next[s.id] = decision;
      return next;
    });
  }, [items]);

  const applyItems = useCallback(async (subset: Item[]) => {
    const byContainer = new Map<string, Item[]>();
    for (const it of subset) byContainer.set(it.container, [...(byContainer.get(it.container) || []), it]);

    let applied = 0;
    let touched = false;
    let didValidation = false;
    const newlyDismissed: string[] = [];

    for (const [container, group] of Array.from(byContainer.entries())) {
      const decided = group.filter((it) => decisions[it.id]);
      if (decided.length === 0) continue;
      touched = true;
      setBusyContainer(container);
      try {
        if (group[0].origin === 'dedup') {
          // container is the caseId; proposed updates live in the case's metadata.
          const payload = decided.map((it) => ({ id: it.id, decision: decisions[it.id] }));
          const res = await API.post(`/api/dedup/cases/${container}/updates/apply`, { decisions: payload });
          applied += res.data?.appliedCount ?? 0;
        } else {
          // validation: apply approved field(s); rejected ones are dismissed locally.
          const approvedFields = Array.from(new Set(decided.filter((it) => decisions[it.id] === 'APPROVED').map((it) => it.field)));
          if (approvedFields.length > 0) {
            const res = await validationApi.applySuggestions(container, approvedFields);
            applied += res.applied?.length ?? approvedFields.length;
          }
          decided.filter((it) => decisions[it.id] === 'REJECTED').forEach((it) => newlyDismissed.push(it.id));
          didValidation = true;
        }
      } catch {
        showToast('error', 'Failed to apply some updates');
      } finally {
        setBusyContainer(null);
      }
    }

    if (!touched) { showToast('error', 'Approve or reject an update first'); return; }
    if (newlyDismissed.length) setDismissed((prev) => new Set([...Array.from(prev), ...newlyDismissed]));
    showToast('success', `Applied ${applied} update${applied === 1 ? '' : 's'}`);
    await refresh();
    if (didValidation) queryClient.invalidateQueries({ queryKey: ['validation-status', caseId] });
    onApplied?.();
  }, [decisions, refresh, onApplied, showToast, queryClient, caseId]);

  // Direct per-suggestion action — apply or dismiss a coupled group immediately (no mark-then-batch).
  const decideAndApply = useCallback(async (subset: Item[], decision: Decision) => {
    if (subset.length === 0) return;
    const container = subset[0].container;
    const origin = subset[0].origin;
    setBusyContainer(container);
    const newlyDismissed: string[] = [];
    let applied = 0;
    try {
      if (origin === 'dedup') {
        const res = await API.post(`/api/dedup/cases/${container}/updates/apply`, {
          decisions: subset.map((it) => ({ id: it.id, decision })),
        });
        applied = res.data?.appliedCount ?? 0;
      } else if (decision === 'APPROVED') {
        const fields = Array.from(new Set(subset.map((it) => it.field)));
        const res = await validationApi.applySuggestions(container, fields);
        applied = res.applied?.length ?? fields.length;
      } else {
        subset.forEach((it) => newlyDismissed.push(it.id));
      }
      showToast('success', decision === 'APPROVED' ? `Applied ${applied || subset.length} update${(applied || subset.length) === 1 ? '' : 's'}` : 'Dismissed');
    } catch {
      showToast('error', 'Failed to apply update');
    } finally {
      setBusyContainer(null);
    }
    if (newlyDismissed.length) setDismissed((prev) => new Set([...Array.from(prev), ...newlyDismissed]));
    await refresh();
    if (origin === 'validation') queryClient.invalidateQueries({ queryKey: ['validation-status', caseId] });
    onApplied?.();
  }, [refresh, onApplied, showToast, queryClient, caseId]);

  const forFields = useCallback((fields: string[]) => items.filter((i) => fields.includes(i.field)), [items]);
  const forSection = useCallback((section: Section) => items.filter((i) => sectionOf(i.field) === section), [items]);

  const value: Ctx = {
    canReview, loading, items, substantiveCount, decisions, busyContainer,
    setDecision, applyItems, decideAndApply, forFields, forSection, refresh,
  };

  return <SuggestionsContext.Provider value={value}>{children}</SuggestionsContext.Provider>;
}

// ---------------------------------------------------------------------------
// Shared rendering — a list of items grouped by source, with controls
// ---------------------------------------------------------------------------

// Human-readable summary of a new entity (instead of raw JSON).
function describeEntity(field: string, value: unknown): { noun: string; title: string; subtitle: string } {
  const o = (value && typeof value === 'object' ? value : {}) as Record<string, any>;
  const join = (arr: (string | null | undefined)[]) => arr.filter(Boolean).join(' · ');
  const asList = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : v ? [String(v)] : []);
  if (field.startsWith('victims')) {
    if (o.isGroup) {
      const counts = [
        o.affectedIndividualsCount != null ? `${o.affectedIndividualsCount} individuals` : null,
        o.affectedFamiliesCount != null ? `${o.affectedFamiliesCount} families` : null,
        o.affectedCommunitiesCount != null ? `${o.affectedCommunitiesCount} communities` : null,
      ];
      return { noun: 'victim group', title: asList(o.groupName).join(', ') || 'Group', subtitle: join(counts) };
    }
    const name = `${o.firstName || ''} ${o.lastName || ''}`.trim() || 'Unnamed victim';
    return { noun: 'victim', title: name, subtitle: join([o.communityRole, o.indigenousGroupOfVictim, o.gender && o.gender !== 'UNKNOWN' ? o.gender : null]) };
  }
  if (field.startsWith('perpetrators')) {
    const name = o.name || `${o.firstName || ''} ${o.lastName || ''}`.trim() || 'Perpetrator';
    return { noun: 'perpetrator', title: name, subtitle: join([o.type, o.position, o.affiliation]) };
  }
  if (field.startsWith('violationDetails')) {
    const detail = (o.perpetratorDetails || o.howKillingCarriedOut || o.cause || '') as string;
    return { noun: 'violation', title: o.violationType || 'Violation detail', subtitle: detail.slice(0, 140) };
  }
  return { noun: 'item', title: fmt(value), subtitle: '' };
}

function ItemRow({ item }: { item: Item }) {
  const isEntity = isEntityField(item.field);
  const cfg = typeConfig[item.type] || typeConfig.fill;
  const [showOld, setShowOld] = useState(false);
  const hasOld = !isEntity && !isEmptyVal(item.currentValue);
  const entityVals = isEntity ? (Array.isArray(item.suggestedValue) ? item.suggestedValue : [item.suggestedValue]) : null;
  const src = item.sourceUrl;
  return (
    <div className="mb-2 last:mb-0">
      {/* 1. description first (plain-language explanation) */}
      {item.reason && <div className="text-sm text-gray-700 dark:text-gray-300 mb-1.5">{item.reason}</div>}

      {/* 2. field label */}
      <div className="flex items-center gap-2 mb-1">
        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${cfg.color}`}>{cfg.label}</span>
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">{prettyField(item.field)}</span>
        {item.groupId && <span className="text-xs text-gray-400">(linked)</span>}
      </div>

      {/* 3. proposed value */}
      {entityVals ? (
        <div className="space-y-1">
          {entityVals.map((v, i) => {
            const e = describeEntity(item.field, v);
            return (
              <div key={i}>
                <div className="text-sm text-gray-900 dark:text-gray-100">
                  <span className="text-xs text-gray-500">Add {e.noun}:</span> <span className="font-medium">{e.title}</span>
                </div>
                {e.subtitle && <div className="text-xs text-gray-500 dark:text-gray-400">{e.subtitle}</div>}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{fmt(item.suggestedValue)}</div>
      )}

      {/* 4. snippet — source (per suggestion) */}
      {(item.snippet || src) && (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {item.snippet && <span className="italic">“{item.snippet}”</span>}
          {src && (<>{item.snippet ? ' — ' : ''}<a href={src} target="_blank" rel="noreferrer" className="text-indigo-500 hover:text-indigo-700 break-all">{urlSnippet(src)}</a></>)}
        </div>
      )}

      {/* 5. former value — toggle to compare (only when there is a real prior value) */}
      {hasOld && (
        <div className="mt-1">
          <button type="button" onClick={() => setShowOld((s) => !s)} className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600">
            <ClockIcon className="h-3 w-3" /> {showOld ? 'Hide former value' : 'See former value'}
          </button>
          {showOld && <div className="mt-0.5 text-xs text-gray-400">was: <span className="line-through">{fmt(item.currentValue)}</span></div>}
        </div>
      )}
    </div>
  );
}

// Renders each suggestion as its own card (consistent with the AI Validation panel), with a direct
// per-suggestion Apply / Dismiss. Coupled suggestions (shared groupId) live in one card.
export function SuggestionList({ items }: { items: Item[] }) {
  const { decideAndApply, busyContainer, canReview } = useSuggestions();

  const cards = useMemo(() => {
    const m = new Map<string, Item[]>();
    for (const it of [...items].sort((a, b) => (a.priority ?? 2) - (b.priority ?? 2))) {
      // Couple fields that are two halves of one logical change so they share a card instead of
      // looking like duplicates. An "OTHER" violation is recorded as OTHER in `violationTypes` PLUS
      // its label in `otherViolationTypes`; group them even when the source didn't set a groupId.
      const coupleKey = COUPLED_FIELD_KEY[it.field];
      const key = `${it.container}::${it.groupId || coupleKey || it.id}`;
      m.set(key, [...(m.get(key) || []), it]);
    }
    return Array.from(m.values());
  }, [items]);

  if (items.length === 0) return null;

  return (
    <div className="space-y-2">
      {cards.map((coupled, gi) => {
        const container = coupled[0].container;
        const busy = busyContainer === container;
        const hasConflict = coupled.some((s) => s.type === 'conflict');
        return (
          <div key={gi} className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
            {coupled.map((it) => <ItemRow key={it.id} item={it} />)}
            {canReview && (
              <div className="flex items-center gap-2 mt-2">
                {/* Direct per-suggestion action — applies/dismisses immediately (like AI Validation). */}
                <button
                  onClick={() => decideAndApply(coupled, 'REJECTED')}
                  disabled={busy}
                  className="px-3 py-1 text-xs font-medium rounded-md border bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  {hasConflict ? 'Keep current' : 'Dismiss'}
                </button>
                <button
                  onClick={() => decideAndApply(coupled, 'APPROVED')}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {busy ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <CheckCircleIcon className="h-3.5 w-3.5" />}
                  {hasConflict ? 'Use new' : 'Apply'}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline badge — sits next to a field / section. Opens a popover with controls.
// ---------------------------------------------------------------------------

function useClickOutside<T extends HTMLElement>(ref: React.RefObject<T | null>, onOutside: () => void, active: boolean) {
  useEffect(() => {
    if (!active) return;
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onOutside(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onOutside, active]);
}

export function FieldSuggestion(props: { field?: string | string[]; section?: Section; align?: 'left' | 'right' }) {
  const { forFields, forSection } = useSuggestions();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  useClickOutside(wrapRef, () => setOpen(false), open);

  const fields = props.field ? (Array.isArray(props.field) ? props.field : [props.field]) : [];
  const items = props.section ? forSection(props.section) : forFields(fields);
  if (items.length === 0) return null;

  const align = props.align || 'left';
  return (
    <span ref={wrapRef} className="relative inline-flex align-middle ml-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={`${items.length} AI suggestion${items.length === 1 ? '' : 's'} from another source`}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-indigo-100 text-indigo-700 hover:bg-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-300"
      >
        <SparklesIcon className="h-3 w-3" />
        {items.length}
      </button>
      {open && (
        <div className={`absolute z-30 mt-6 top-0 ${align === 'right' ? 'right-0' : 'left-0'} w-[36rem] max-w-[92vw] max-h-[70vh] overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-3`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-900 dark:text-gray-100">
              <SparklesIcon className="h-3.5 w-3.5 text-indigo-500" /> AI Suggestion{items.length === 1 ? '' : 's'}
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-4 w-4" /></button>
          </div>
          <SuggestionList items={items} />
        </div>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Card — an always-visible panel of dedup (related-source) suggestions, shown
// above the case information. Replaces the compact badge/modal. Renders nothing
// when there are no substantive related-source suggestions.
// ---------------------------------------------------------------------------

export function SuggestionsCard() {
  const { items } = useSuggestions();
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerShown, setDrawerShown] = useState(false); // drives the slide-in/out animation
  const openDrawer = () => { setModalOpen(true); requestAnimationFrame(() => setDrawerShown(true)); };
  const closeDrawer = () => { setDrawerShown(false); setTimeout(() => setModalOpen(false), 300); };

  const dedupItems = items.filter((i) => i.origin === 'dedup');
  const proposedCount = dedupItems.filter((i) => i.type !== 'source').length;
  if (proposedCount === 0) return null;

  return (
    <>
      {/* Sidebar summary card — clickable, opens a right slide-over (mirrors AI Validation) */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
            <SparklesIcon className="h-4 w-4 text-indigo-500" /> AI Suggestions from related sources
          </h3>
          <span className="inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">{proposedCount}</span>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Proposed updates from duplicate / related sources. Nothing changes until you apply.</p>
        <button
          onClick={openDrawer}
          className="w-full px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 rounded-md hover:bg-indigo-100"
        >
          Review suggestions
        </button>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className={`fixed inset-0 bg-gray-500/60 transition-opacity duration-300 ${drawerShown ? 'opacity-100' : 'opacity-0'}`}
            onClick={closeDrawer}
          />
          <div
            className={`fixed inset-y-0 right-0 w-full max-w-3xl bg-white dark:bg-gray-800 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${drawerShown ? 'translate-x-0' : 'translate-x-full'}`}
          >
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between shrink-0">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-indigo-500" /> AI Suggestions from related sources
              </h3>
              <button onClick={closeDrawer} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Nothing changes until you apply.</p>
              <SuggestionList items={dedupItems} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

